import { useEffect, useState } from 'preact/hooks';
import { NewMark } from '../releases.js';
import type { Api } from '../api.js';
import { ApiError } from '../api.js';
import { when } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { Icon } from '../shell.js';
import { navigate } from '../router.js';
import { Callout, Dialog, LoadError, Loading } from '../ui.js';
import { Tip, TIPS } from '../guide.js';
import { MoveToHubNote, moveToHubLabel } from './sendForReview.js';

/**
 * The project's Files tab (Andreas 2026-10-15; Del 1 UX-133/UX-332): the Drive folders, Ziflow review folder and brief
 * as links, "Open in Ziflow" per video, and the Drive folders' contents read-only – read as the signed-in person, so
 * nobody sees more than Drive already shows them. With review in the Hub on (Settings), files can be ticked and sent to
 * the Hub's own review as a draft or final formats (UX-139/UX-144); the Hub never makes Ziflow proofs (owner 2026-10-26). *Upload…* puts files from the computer into the folder on show,
 * written as the person (FLOW-601), so they can be sent for review from there; the Hub writes nothing else to Drive.
 */

export interface ProjectFilesData {
  links: { driveInternal: string | null; driveExternal: string | null; ziflowFolder: string | null; brief: string | null };
  drive: { configured: boolean; canBrowse: boolean; roots: { internal: string | null; external: string | null }; start?: { id: string; name: string }[] };
  videos: { id: string; code: string; name: string; proof: boolean }[];
  ziflow: { personalLinks: boolean };
  /** "Send for review" is on and this person can use it */
  review?: { on: boolean; inZiflow?: boolean };
  /** The Drive folders the Hub makes for a project made in the Hub (owner 2026-10-19) */
  folders?: FolderState & { on: boolean; hub: boolean; when: 'real' | 'created'; test: boolean; canEdit: boolean };
}
export interface FolderState {
  state: 'none' | 'pending' | 'ready' | 'rename_blocked' | 'failed';
  name: string | null;
  pendingName: string | null;
  error: string | null;
  requested: boolean;
  customName: boolean;
}
export interface DriveItem {
  id: string;
  name: string;
  folder: boolean;
  mimeType: string;
  size: number | null;
  modifiedTime: string | null;
  webViewLink: string | null;
}
export interface DriveListing {
  id: string;
  name: string;
  webViewLink: string | null;
  items: DriveItem[];
  truncated: boolean;
  root: Root;
}
type Root = 'internal' | 'external';

const TX = texts(
  {
    root: { internal: 'Internal', external: 'Client folder' } as Record<Root, string>,
    kind: { folder: 'Folder', video: 'Video', image: 'Image', audio: 'Audio', file: 'File' },
    links: 'Links',
    linkRows: [
      ['Internal Drive folder', 'Rushes, project files and exports'],
      ['Client Drive folder', 'What the client sees'],
      ['Ziflow review folder', 'All the project’s proofs'],
      ['Brief', 'The video brief']
    ] as [string, string][],
    notSet: 'Not set in Monday',
    openNewTab: (label: string) => `Open ${label} (new tab)`,
    open: 'Open',
    openingZiflow: 'Opening Ziflow…',
    reviewTitle: 'Review in Ziflow',
    personal: 'Opens the newest version with your own Ziflow login.',
    folderOnly: 'Opens the project’s review folder in Ziflow.',
    inZiflow: 'This project is reviewed in Ziflow. When you send the next version for review from the Hub, the project moves to the Hub and the client gets a new link.',
    noProof: 'No proof yet',
    openInZiflowLabel: (name: string) => `Open ${name} in Ziflow (new tab)`,
    opening: 'Opening…',
    openInZiflow: 'Open in Ziflow',
    noVideos: 'No videos yet.',
    uploadTo: (where: string) => `Upload files to ${where}`,
    uploading: 'Uploading…',
    upload: 'Upload',
    toWhere: (where: string) => ` to ${where}`,
    inDrive: 'In Drive',
    waiting: 'Waiting',
    progress: (pct: number, of: string) => `${pct} % of ${of}`,
    uploadingName: (name: string) => `Uploading ${name}`,
    clear: 'Clear',
    folder: 'Folder',
    noFolder: 'This project has no Drive folder linked in Monday yet.',
    notConnected: 'Drive is not connected to the Hub yet',
    useLinks: 'Use the folder links for now.',
    kffAccount: 'Browse with a KFF Google account',
    useLinksDrive: 'Use the folder links to open the project in Drive.',
    folderPath: 'Folder path',
    openInDrive: 'Open in Drive',
    sendForReview: 'Send for review',
    ticked: (n: number) => `${n} file${n === 1 ? '' : 's'} ticked`,
    tickHint: 'Tick files to send them for review.',
    sendForReviewBtn: 'Send for review…',
    sendFinalBtn: 'Send final formats…',
    noAnswer: 'Drive did not answer',
    readingDrive: 'Reading Drive…',
    emptyFolder: 'This folder is empty.',
    filesIn: (name: string) => `Files in ${name}`,
    send: 'Send',
    name: 'Name',
    type: 'Type',
    modified: 'Modified',
    size: 'Size',
    sendName: (name: string) => `Send ${name}`,
    truncated: 'Showing the first 200. Open the folder in Drive for the rest.',
    sendFinal: 'Send final formats',
    done: 'Done',
    cancel: 'Cancel',
    sending: 'Sending…',
    sendForReviewNow: 'Send for review',
    inHub: 'sent for review in the Hub. ',
    openReview: 'Open the review',
    notSent: 'Not sent: ',
    unreadable: 'The files could not be read',
    readingFiles: 'Reading the files…',
    draftInfo: 'Each file becomes the next version of its video in the Hub’s review. One file per video. A file that matches no video becomes a new video.',
    finalInfo: 'Each video gets one version with all its final formats. The producer decides first, then the client.',
    pickTheVideo: 'Pick the video',
    matchedByName: 'Matched by name',
    newVideoTag: 'New video',
    pickedByYou: 'Picked by you',
    videoFor: (name: string) => `Video for ${name}`,
    pickVideo: 'Pick a video…',
    newVideoOpt: (name: string) => `New video: ${name}`,
    formatOf: (name: string) => `Format of ${name}`,
    remove: (name: string) => `Remove ${name}`,
    onePerVideo: 'One file per video for drafts.',
    foldersTitle: 'Drive folders',
    foldersWaitReal: 'The Hub makes the folders when the project becomes real: when someone is put on it, a shoot is booked, a version is sent or the delivery month begins.',
    foldersWaitCreated: 'The Hub makes the folders within 15 minutes.',
    foldersTest: 'A test project: the folders go to zz_Hubben test in Drive.',
    foldersNow: 'Create folders now',
    foldersMaking: 'Creating…',
    foldersHalf: 'Part of the folders are made; the rest follow within 15 minutes.',
    foldersFailed: 'The folders could not be made',
    foldersRetry: 'Try again',
    folderName: 'Folder name',
    rename: 'Rename',
    renameSave: 'Save name',
    renameHint: 'Both the internal folder and the client folder get the name. New project names no longer rename it.',
    blockedTitle: 'The folder keeps its old name for now',
    blockedBody: (to: string) => `The project has a new name or month, but RUSHES or ADOBE already hold files. Renaming moves the paths in Drive for desktop, so Premiere has to find its media again. New name: ${to}`,
    renameAnyway: 'Rename anyway',
    keepName: 'Keep the old name'
  },
  {
    root: { internal: 'Intern', external: 'Kundmapp' },
    kind: { folder: 'Mapp', video: 'Video', image: 'Bild', audio: 'Ljud', file: 'Fil' },
    links: 'Länkar',
    linkRows: [
      ['Intern Drive-mapp', 'Rushes, projektfiler och exporter'],
      ['Kundens Drive-mapp', 'Det kunden ser'],
      ['Ziflow-mapp för granskning', 'Alla projektets korrektur'],
      ['Brief', 'Videobriefen']
    ],
    notSet: 'Inte angiven i Monday',
    openNewTab: (label: string) => `Öppna ${label} (ny flik)`,
    open: 'Öppna',
    openingZiflow: 'Öppnar Ziflow…',
    reviewTitle: 'Granska i Ziflow',
    personal: 'Öppnar den senaste versionen med din egen Ziflow-inloggning.',
    folderOnly: 'Öppnar projektets granskningsmapp i Ziflow.',
    inZiflow: 'Projektet granskas i Ziflow. När du skickar nästa version till granskning från Hubben flyttas projektet till Hubben och kunden får en ny länk.',
    noProof: 'Inget korrektur än',
    openInZiflowLabel: (name: string) => `Öppna ${name} i Ziflow (ny flik)`,
    opening: 'Öppnar…',
    openInZiflow: 'Öppna i Ziflow',
    noVideos: 'Inga videor än.',
    uploadTo: (where: string) => `Ladda upp filer till ${where}`,
    uploading: 'Laddar upp…',
    upload: 'Ladda upp',
    toWhere: (where: string) => ` till ${where}`,
    inDrive: 'I Drive',
    waiting: 'Väntar',
    progress: (pct: number, of: string) => `${pct} % av ${of}`,
    uploadingName: (name: string) => `Laddar upp ${name}`,
    clear: 'Rensa',
    folder: 'Mapp',
    noFolder: 'Projektet har ingen Drive-mapp länkad i Monday än.',
    notConnected: 'Drive är inte anslutet till Hubben än',
    useLinks: 'Använd mapplänkarna så länge.',
    kffAccount: 'Bläddra med ett Google-konto från KFF',
    useLinksDrive: 'Använd mapplänkarna för att öppna projektet i Drive.',
    folderPath: 'Mappsökväg',
    openInDrive: 'Öppna i Drive',
    sendForReview: 'Skicka för granskning',
    ticked: (n: number) => `${n} ${n === 1 ? 'fil markerad' : 'filer markerade'}`,
    tickHint: 'Markera filer för att skicka dem för granskning.',
    sendForReviewBtn: 'Skicka för granskning…',
    sendFinalBtn: 'Skicka slutformat…',
    noAnswer: 'Drive svarade inte',
    readingDrive: 'Läser Drive…',
    emptyFolder: 'Mappen är tom.',
    filesIn: (name: string) => `Filer i ${name}`,
    send: 'Skicka',
    name: 'Namn',
    type: 'Typ',
    modified: 'Ändrad',
    size: 'Storlek',
    sendName: (name: string) => `Skicka ${name}`,
    truncated: 'Visar de första 200. Öppna mappen i Drive för resten.',
    sendFinal: 'Skicka slutformat',
    done: 'Klar',
    cancel: 'Avbryt',
    sending: 'Skickar…',
    sendForReviewNow: 'Skicka för granskning',
    inHub: 'skickad för granskning i Hubben. ',
    openReview: 'Öppna granskningen',
    notSent: 'Inte skickad: ',
    unreadable: 'Filerna gick inte att läsa',
    readingFiles: 'Läser filerna…',
    draftInfo: 'Varje fil blir nästa version av sin video i Hubbens granskning. En fil per video. En fil som inte matchar någon video blir en ny video.',
    finalInfo: 'Varje video får en version med alla sina slutformat. Producenten beslutar först, sedan kunden.',
    pickTheVideo: 'Välj video',
    matchedByName: 'Matchad på namn',
    newVideoTag: 'Ny video',
    pickedByYou: 'Vald av dig',
    videoFor: (name: string) => `Video för ${name}`,
    pickVideo: 'Välj en video…',
    newVideoOpt: (name: string) => `Ny video: ${name}`,
    formatOf: (name: string) => `Format för ${name}`,
    remove: (name: string) => `Ta bort ${name}`,
    onePerVideo: 'En fil per video för utkast.',
    foldersTitle: 'Drive-mappar',
    foldersWaitReal: 'Hubben skapar mapparna när projektet blir på riktigt: när någon läggs till, en inspelning bokas, en version skickas eller leveransmånaden börjar.',
    foldersWaitCreated: 'Hubben skapar mapparna inom 15 minuter.',
    foldersTest: 'Ett testprojekt: mapparna hamnar i zz_Hubben test i Drive.',
    foldersNow: 'Skapa mappar nu',
    foldersMaking: 'Skapar…',
    foldersHalf: 'En del av mapparna är skapade; resten kommer inom 15 minuter.',
    foldersFailed: 'Mapparna kunde inte skapas',
    foldersRetry: 'Försök igen',
    folderName: 'Mappnamn',
    rename: 'Byt namn',
    renameSave: 'Spara namnet',
    renameHint: 'Både den interna mappen och kundmappen får namnet. Nya projektnamn byter sedan inte namn på den.',
    blockedTitle: 'Mappen behåller sitt gamla namn tills vidare',
    blockedBody: (to: string) => `Projektet har fått nytt namn eller ny månad, men RUSHES eller ADOBE har redan filer. Ett namnbyte flyttar sökvägarna i Drive for desktop, så Premiere måste hitta sitt material igen. Nytt namn: ${to}`,
    renameAnyway: 'Byt namn ändå',
    keepName: 'Behåll det gamla namnet'
  }
);

/** "170 MB" */
export function size(n: number | null, lang: Lang = 'en'): string {
  if (n === null) return '–';
  if (n < 1000) return `${n} B`;
  const u = ['kB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = -1;
  do {
    v /= 1000;
    i++;
  } while (v >= 1000 && i < u.length - 1);
  const num = String(v >= 10 ? Math.round(v) : Math.round(v * 10) / 10);
  return `${lang === 'sv' ? num.replace('.', ',') : num} ${u[i]}`;
}

/** A short word for what the file is, from Drive's MIME type. */
export function kindOf(i: Pick<DriveItem, 'folder' | 'mimeType' | 'name'>, lang: Lang = 'en'): string {
  const k = TX[lang].kind;
  if (i.folder) return k.folder;
  const m = i.mimeType;
  if (m.startsWith('video/')) return k.video;
  if (m.startsWith('image/')) return k.image;
  if (m.startsWith('audio/')) return k.audio;
  if (m === 'application/pdf') return 'PDF';
  if (m.includes('google-apps.document')) return 'Google Doc';
  if (m.includes('google-apps.spreadsheet')) return 'Google Sheet';
  if (m.includes('google-apps.presentation')) return 'Google Slides';
  const ext = /\.([a-z0-9]{1,6})$/i.exec(i.name)?.[1];
  return ext ? ext.toUpperCase() : k.file;
}

/** File names have no spaces: let them wrap after _ - . so a narrow screen breaks between words, not inside them. */
export function breakable(name: string) {
  return name.split(/(?<=[_\-.])/).map((part, k) => (k ? [<wbr />, part] : part));
}

const hasSize = (i: DriveItem) => !i.folder && !i.mimeType.startsWith('application/vnd.google-apps.');

/** "Video · 170 MB · 9 Oct, 16:12": the row's columns as one line, for narrow screens. */
export function metaOf(i: DriveItem, lang: Lang = 'en'): string {
  return [kindOf(i, lang), hasSize(i) ? size(i.size, lang) : '', i.modifiedTime ? when(i.modifiedTime, lang) : ''].filter(Boolean).join(' · ');
}

/** A file Ziflow can be sent: not a folder, not a Google Doc. */
const sendable = (i: DriveItem) => !i.folder && !i.mimeType.startsWith('application/vnd.google-apps.');

const iconOf = (i: DriveItem) => (i.folder ? 'folder' : i.mimeType.startsWith('video/') ? 'film' : i.mimeType.startsWith('image/') ? 'image' : 'file-text');

export function ProjectFiles(p: { api: Api; id: string }) {
  const [data, setData] = useState<ProjectFilesData | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [gen, setGen] = useState(0);
  useEffect(() => {
    p.api.get<ProjectFilesData>(`/api/projects/${p.id}/files`).then(setData, setErr);
  }, [p.id, gen]);
  if (err) return <LoadError error={err} />;
  if (!data) return <Loading />;
  return (
    <div class="pd pfl">
      <DriveBrowser key={`${data.links.driveInternal ?? ''}|${gen}`} api={p.api} id={p.id} data={data} />
      <div class="pd-side">
        {data.folders?.on && data.folders.hub ? <Folders api={p.api} id={p.id} f={data.folders} onChange={() => setGen((g) => g + 1)} /> : null}
        <Links links={data.links} />
        <Review api={p.api} id={p.id} data={data} />
      </div>
    </div>
  );
}

/** The Drive folders the Hub makes (owner 2026-10-19): where they stand, "Create folders now" and the folder's name. */
function Folders(p: { api: Api; id: string; f: NonNullable<ProjectFilesData['folders']>; onChange: () => void }) {
  const t = TX[useLang()];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.f.name ?? '');
  const f = p.f;
  const run = async (fn: () => Promise<{ error?: string | null }>) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (r.error) setError(r.error);
      setEditing(false);
      p.onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const make = () => run(() => p.api.post<{ error?: string | null }>(`/api/projects/${p.id}/drive-folders`));
  const rename = (body: { name?: string; confirm?: boolean }) => run(() => p.api.put<{ error?: string | null }>(`/api/projects/${p.id}/drive-folders`, body));
  const waiting = f.state === 'none' || f.state === 'pending';
  return (
    <section class="blk pfl-folders" aria-labelledby="pfl-folders-h">
      <h2 class="blk-title" id="pfl-folders-h">
        {t.foldersTitle}
      </h2>
      {f.test ? <p class="small muted">{t.foldersTest}</p> : null}
      {waiting ? (
        <>
          <p class="small">{f.requested ? t.foldersHalf : f.when === 'created' ? t.foldersWaitCreated : t.foldersWaitReal}</p>
          {f.canEdit ? (
            <button type="button" class="btn btn-sm" disabled={busy} onClick={() => void make()}>
              {busy ? t.foldersMaking : t.foldersNow}
            </button>
          ) : null}
        </>
      ) : f.state === 'failed' ? (
        <Callout tone="warn" title={t.foldersFailed}>
          <p class="small">{f.error}</p>
          {f.canEdit ? (
            <button type="button" class="btn btn-sm" disabled={busy} onClick={() => void make()}>
              {busy ? t.foldersMaking : t.foldersRetry}
            </button>
          ) : null}
        </Callout>
      ) : (
        <>
          {f.state === 'rename_blocked' && f.pendingName ? (
            <Callout tone="warn" title={t.blockedTitle}>
              <p class="small">{t.blockedBody(f.pendingName)}</p>
              {f.canEdit ? (
                <div class="pfl-actions">
                  <button type="button" class="btn btn-sm" disabled={busy} onClick={() => void rename({ confirm: true })}>
                    {t.renameAnyway}
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" disabled={busy} onClick={() => void rename({ name: f.name ?? '', confirm: true })}>
                    {t.keepName}
                  </button>
                </div>
              ) : null}
            </Callout>
          ) : null}
          {editing ? (
            <div class="field">
              <label class="label" for="pfl-folder-name">
                {t.folderName}
              </label>
              <input id="pfl-folder-name" class="input input--wide" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} aria-describedby="pfl-folder-hint" />
              <span class="hint" id="pfl-folder-hint">
                {t.renameHint}
              </span>
              <div class="pfl-actions">
                <button type="button" class="btn btn-sm btn-primary" disabled={busy || !name.trim()} onClick={() => void rename({ name: name.trim() })}>
                  {t.renameSave}
                </button>
                <button type="button" class="btn btn-sm btn-ghost" disabled={busy} onClick={() => setEditing(false)}>
                  {t.cancel}
                </button>
              </div>
            </div>
          ) : (
            <p class="pfl-folder-name">
              <span>
                <span class="pfl-link-k">{t.folderName}</span>
                <span class="small muted pfl-wrap">{breakable(f.name ?? '')}</span>
              </span>
              {f.canEdit ? (
                <button type="button" class="btn btn-sm" onClick={() => (setName(f.name ?? ''), setEditing(true))}>
                  {t.rename}
                </button>
              ) : null}
            </p>
          )}
        </>
      )}
      {error ? <p class="small warn" role="alert">{error}</p> : null}
    </section>
  );
}

function Links(p: { links: ProjectFilesData['links'] }) {
  const t = TX[useLang()];
  const urls = [p.links.driveInternal, p.links.driveExternal, p.links.ziflowFolder, p.links.brief];
  const rows: [string, string, string | null][] = t.linkRows.map(([label, sub], k) => [label, sub, urls[k] ?? null]);
  return (
    <section class="blk" aria-labelledby="pfl-links-h">
      <h2 class="blk-title" id="pfl-links-h">
        {t.links}
      </h2>
      <ul class="pfl-links">
        {rows.map(([label, sub, url]) => (
          <li class="pfl-link">
            <span>
              <span class="pfl-link-k">{label}</span>
              <span class="small muted">{url ? sub : t.notSet}</span>
            </span>
            {url ? (
              <a class="btn btn-sm" href={url} target="_blank" rel="noopener noreferrer" aria-label={t.openNewTab(label)}>
                {t.open} <Icon name="external-link" />
              </a>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Review(p: { api: Api; id: string; data: ProjectFilesData }) {
  const t = TX[useLang()];
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ id: string; text: string } | null>(null);
  const videos = p.data.videos;
  const folder = p.data.links.ziflowFolder;
  // the tab opens before the request so the browser does not block it; the link is minted for the viewer's own email
  const open = async (videoId: string) => {
    if (busy) return;
    setError(null);
    setBusy(videoId);
    const w = window.open('', '_blank');
    if (w) {
      w.opener = null;
      try {
        w.document.title = t.openingZiflow;
        w.document.body.textContent = t.openingZiflow;
      } catch {
        // a browser that blocks access to the new tab still navigates it below
      }
    }
    try {
      const r = await p.api.post<{ url: string; personal: boolean }>(`/api/projects/${p.id}/videos/${videoId}/ziflow`);
      if (w && !w.closed) w.location.href = r.url;
      else location.assign(r.url);
    } catch (e) {
      w?.close();
      setError({ id: videoId, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <section class="blk" aria-labelledby="pf-zf-h">
      <h2 class="blk-title" id="pf-zf-h">
        {t.reviewTitle}
      </h2>
      {p.data.review?.inZiflow ? <p class="small">{t.inZiflow}</p> : null}
      <p class="blk-info">{p.data.ziflow.personalLinks ? t.personal : t.folderOnly}</p>
      {videos.length ? (
        <ul class="pfl-links">
          {videos.map((v) => (
            <li class="pfl-link">
              <span>
                <span class="pfl-link-k" title={v.code}>
                  {v.name}
                </span>
                {error?.id === v.id ? (
                  <span class="small pfl-err" role="alert">
                    {error.text}
                  </span>
                ) : !v.proof ? (
                  <span class="small muted">{t.noProof}</span>
                ) : null}
              </span>
              {v.proof || folder ? (
                <button type="button" class="btn btn-sm" disabled={Boolean(busy)} aria-label={t.openInZiflowLabel(v.name)} onClick={() => void open(v.id)}>
                  {busy === v.id ? t.opening : t.openInZiflow}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p class="muted">{t.noVideos}</p>
      )}
    </section>
  );
}

type UploadRow = { name: string; size: number; sent: number; state: 'waiting' | 'uploading' | 'done' | 'failed'; error?: string };

/** Sends one file to the Hub in chunks; the Hub passes each to Drive. Returns the new Drive file. */
export async function uploadFile(api: Api, projectId: string, target: { root: Root; folder?: string }, file: File, progress: (sent: number) => void): Promise<{ id: string; name: string }> {
  const s = await api.post<{ token: string; chunk: number }>(`/api/projects/${projectId}/files/upload`, { ...target, name: file.name, size: file.size, mimeType: file.type || undefined });
  let offset = 0;
  for (;;) {
    const part = file.slice(offset, Math.min(offset + s.chunk, file.size));
    const q = new URLSearchParams({ t: s.token, offset: String(offset) });
    const step = await api.putBytes<{ done: false; received: number } | { done: true; file: { id: string; name: string } }>(`/api/projects/${projectId}/files/upload?${q}`, part);
    if (step.done) {
      progress(file.size);
      return step.file;
    }
    // Google says how much it holds; carry on from there
    offset = step.received;
    progress(offset);
  }
}

function Upload(p: { api: Api; id: string; root: Root; folder: string | undefined; where: string; onDone: (files: { id: string; name: string }[]) => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [rows, setRows] = useState<UploadRow[]>([]);
  const busy = rows.some((r) => r.state === 'waiting' || r.state === 'uploading');
  const start = async (list: FileList | null) => {
    const files = [...(list ?? [])].filter((f) => f.size > 0);
    if (!files.length || busy) return;
    const state: UploadRow[] = files.map((f) => ({ name: f.name, size: f.size, sent: 0, state: 'waiting' }));
    const show = () => setRows(state.map((r) => ({ ...r })));
    show();
    const made: { id: string; name: string }[] = [];
    for (const [i, f] of files.entries()) {
      state[i]!.state = 'uploading';
      show();
      try {
        made.push(
          await uploadFile(p.api, p.id, { root: p.root, ...(p.folder ? { folder: p.folder } : {}) }, f, (sent) => {
            state[i]!.sent = sent;
            show();
          })
        );
        state[i]!.state = 'done';
      } catch (e) {
        state[i]!.state = 'failed';
        state[i]!.error = e instanceof Error ? e.message : String(e);
      }
      show();
    }
    p.onDone(made);
  };
  const pct = (r: UploadRow) => Math.floor((r.sent / r.size) * 100);
  return (
    <div class="pfl-up">
      <label class={`btn btn-sm${busy ? ' is-disabled' : ''}`} title={t.uploadTo(p.where)}>
        <Icon name="upload" /> {busy ? t.uploading : t.upload}
        <span class="sr-only">{t.toWhere(p.where)}</span>
        <input
          type="file"
          multiple
          class="sr-only"
          disabled={busy}
          onChange={(e) => {
            const input = e.target as HTMLInputElement;
            void start(input.files);
            input.value = '';
          }}
        />
      </label>
      {rows.length ? (
        <ul class="pfl-up-rows" aria-live="polite">
          {rows.map((r) => (
            <li class="pfl-up-row">
              <span class="pfl-up-name">{r.name}</span>
              {r.state === 'failed' ? (
                <span class="small pfl-err" role="alert">
                  {r.error}
                </span>
              ) : (
                <span class="small muted">{r.state === 'done' ? t.inDrive : r.state === 'waiting' ? t.waiting : t.progress(pct(r), size(r.size, lang))}</span>
              )}
              {r.state === 'uploading' ? <progress max={100} value={pct(r)} aria-label={t.uploadingName(r.name)} /> : null}
            </li>
          ))}
          {!busy ? (
            <li>
              <button type="button" class="btn btn-sm btn-ghost" onClick={() => setRows([])}>
                {t.clear}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

function DriveBrowser(p: { api: Api; id: string; data: ProjectFilesData }) {
  const lang = useLang();
  const t = TX[lang];
  const ROOT_LABEL = t.root;
  const { drive } = p.data;
  const roots = (['internal', 'external'] as Root[]).filter((r) => drive.roots[r]);
  const [root, setRoot] = useState<Root | null>(roots[0] ?? null);
  // the folders opened below the root, for the breadcrumb (the root itself is the listing's own name)
  // opens on Internal / EXPORTS / VIDEO when the project has them (owner 2026-10-20); the crumbs lead back up
  const [trail, setTrail] = useState<{ id: string; name: string }[]>(roots[0] === 'internal' ? (drive.start ?? []) : []);
  const [list, setList] = useState<DriveListing | null>(null);
  const [err, setErr] = useState<unknown>(null);
  // ticked files, kept while moving between folders: id → name
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  const [send, setSend] = useState<Kind | null>(null);
  // bumped after an upload so the folder is read again
  const [rev, setRev] = useState(0);
  const reviewOn = Boolean(p.data.review?.on);
  const browse = drive.configured && drive.canBrowse && root;
  const folder = trail.at(-1)?.id;
  useEffect(() => {
    if (!browse) return;
    setList(null);
    setErr(null);
    const q = new URLSearchParams({ root: root!, ...(folder ? { folder } : {}) });
    p.api.get<DriveListing>(`/api/projects/${p.id}/drive?${q}`).then(setList, setErr);
  }, [p.id, root, folder, browse, rev]);
  const pick = (r: Root) => {
    setRoot(r);
    setTrail([]);
  };
  const here = list ?? null;
  const tick = (i: DriveItem, on: boolean) => {
    const next = new Map(picked);
    if (on) next.set(i.id, i.name);
    else next.delete(i.id);
    setPicked(next);
  };
  return (
    <section class="blk pfl-drive" aria-labelledby="pfl-drive-h">
      <div class="blk-head">
        <h2 class="blk-title" id="pfl-drive-h">
          Drive
        </h2>
        {browse && roots.length > 1 ? (
          <div class="seg" role="group" aria-label={t.folder}>
            {roots.map((r) => (
              <button type="button" class={`seg-btn seg-btn--sm${r === root ? ' is-on' : ''}`} aria-pressed={r === root} onClick={() => pick(r)}>
                {ROOT_LABEL[r]}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {!roots.length ? (
        <p class="blk-info">{t.noFolder}</p>
      ) : !drive.configured ? (
        <Callout tone="info" title={t.notConnected}>
          {t.useLinks}
        </Callout>
      ) : !drive.canBrowse ? (
        <Callout tone="info" title={t.kffAccount}>
          {t.useLinksDrive}
        </Callout>
      ) : (
        <>
          {root === 'internal' ? (
            <Tip id="folders" title={TIPS[lang].foldersTitle}>
              {TIPS[lang].folders}
            </Tip>
          ) : null}
          <nav class="pfl-crumbs" aria-label={t.folderPath}>
            <button type="button" class="pfl-crumb" aria-current={trail.length ? undefined : 'page'} onClick={() => setTrail([])}>
              {ROOT_LABEL[root!]}
            </button>
            {trail.map((c, i) => (
              <>
                <span aria-hidden="true">/</span>
                <button type="button" class="pfl-crumb" aria-current={i === trail.length - 1 ? 'page' : undefined} onClick={() => setTrail(trail.slice(0, i + 1))}>
                  {c.name}
                </button>
              </>
            ))}
            <Upload
              api={p.api}
              id={p.id}
              root={root!}
              folder={folder}
              where={here?.name ?? ROOT_LABEL[root!]}
              onDone={(files) => {
                setRev((n) => n + 1);
                // ticked straight away, ready to send for review
                if (reviewOn && files.length) {
                  const next = new Map(picked);
                  for (const f of files) next.set(f.id, f.name);
                  setPicked(next);
                }
              }}
            />
            {here?.webViewLink ? (
              <a class="pfl-open small" href={here.webViewLink} target="_blank" rel="noopener noreferrer">
                {t.openInDrive} <Icon name="external-link" />
              </a>
            ) : null}
          </nav>
          {reviewOn ? (
            <div class="pfl-sel" role="group" aria-label={t.sendForReview}>
              <span class="small">{picked.size ? t.ticked(picked.size) : t.tickHint}</span>
              <button type="button" class="btn btn-sm" disabled={!picked.size} onClick={() => setSend('draft')}>
                {t.sendForReviewBtn}
                <NewMark k="send-for-review-files" />
              </button>
              <button type="button" class="btn btn-sm" disabled={!picked.size} onClick={() => setSend('final')}>
                {t.sendFinalBtn}
              </button>
              {picked.size ? (
                <button type="button" class="btn btn-sm btn-ghost" onClick={() => setPicked(new Map())}>
                  {t.clear}
                </button>
              ) : null}
            </div>
          ) : null}
          {err ? (
            <Callout tone="warn" title={err instanceof ApiError ? err.message : t.noAnswer} />
          ) : !here ? (
            <Loading label={t.readingDrive} />
          ) : !here.items.length ? (
            <p class="muted pfl-empty">{t.emptyFolder}</p>
          ) : (
            <div class="tbl-wrap" role="region" aria-label={t.filesIn(here.name)} tabIndex={0}>
              <table class="tbl pfl-tbl">
                <thead>
                  <tr>
                    {reviewOn ? (
                      <th scope="col" class="pfl-col-pick">
                        <span class="sr-only">{t.send}</span>
                      </th>
                    ) : null}
                    <th scope="col">{t.name}</th>
                    <th scope="col" class="pfl-col-kind">
                      {t.type}
                    </th>
                    <th scope="col" class="pfl-col-date">
                      {t.modified}
                    </th>
                    <th scope="col" class="num pfl-col-size">
                      {t.size}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {here.items.map((i) => (
                    <tr>
                      {reviewOn ? (
                        <td class="pfl-col-pick">
                          {sendable(i) ? <input type="checkbox" aria-label={t.sendName(i.name)} checked={picked.has(i.id)} onChange={(e) => tick(i, (e.target as HTMLInputElement).checked)} /> : null}
                        </td>
                      ) : null}
                      <th scope="row">
                        {i.folder ? (
                          <button type="button" class="pfl-name" onClick={() => setTrail([...trail, { id: i.id, name: i.name }])}>
                            <Icon name={iconOf(i)} />
                            <span>{breakable(i.name)}</span>
                          </button>
                        ) : (
                          <a class="pfl-name" href={i.webViewLink ?? undefined} target="_blank" rel="noopener noreferrer">
                            <Icon name={iconOf(i)} />
                            <span>{breakable(i.name)}</span>
                          </a>
                        )}
                        {/* phones: the columns below fold into one line under the name */}
                        <span class="pfl-meta small muted">{metaOf(i, lang)}</span>
                      </th>
                      <td class="pfl-col-kind muted">{kindOf(i, lang)}</td>
                      <td class="num pfl-col-date">{i.modifiedTime ? when(i.modifiedTime, lang) : '–'}</td>
                      <td class="num pfl-col-size">{hasSize(i) ? size(i.size, lang) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {here?.truncated ? <p class="small muted">{t.truncated}</p> : null}
          {send ? (
            <SendDialog
              api={p.api}
              id={p.id}
              kind={send}
              fileIds={[...picked.keys()]}
              onClose={() => setSend(null)}
              onSent={(sent) => {
                const next = new Map(picked);
                for (const f of sent) next.delete(f);
                setPicked(next);
              }}
            />
          ) : null}
        </>
      )}
    </section>
  );
}

type Kind = 'draft' | 'final';
interface Preview {
  kind: Kind;
  videos: { id: string; name: string; code: string }[];
  /** videoId 'new' makes a new video named newName */
  files: { id: string; name: string; size: number | null; videoId: string | null; newName: string; format: string | null; problem: string | null }[];
  /** a Ziflow project: sending moves it to the Hub (owner 2026-11-02) */
  moveToHub?: { waiting: string[] } | null;
}
const NEW = 'new';
interface Result {
  videoId: string | null;
  videoName: string;
  ok: boolean;
  newVersion: boolean;
  files: string[];
  fileIds: string[];
  error: string | null;
  /** review in the Hub: the version that was created */
  versionId?: string | null;
}

/** What the Hub guessed for a row: matched by name, a new video, picked by the person, or still to pick. */
function Guess(p: { row: Preview['files'][number]; suggested: string | null; t: (typeof TX)['en'] }) {
  const { row, t } = p;
  if (!row.videoId) return <span class="pill pill--crit">{t.pickTheVideo}</span>;
  if (row.videoId === NEW) return <span class="pill pill--info">{t.newVideoTag}</span>;
  if (row.videoId === p.suggested) return <span class="pill pill--ok">{t.matchedByName}</span>;
  return <span class="pill pill--neutral">{t.pickedByYou}</span>;
}

/**
 * The send dialog (UX-139): one row per ticked file with the video (and for final formats the format) read from its name,
 * changeable; a file that matches no video becomes a new video named after the file, unless the person picks one. Each video becomes its own
 * version in the Hub's review, so one that fails does not stop the others.
 */
function SendDialog(p: { api: Api; id: string; kind: Kind; fileIds: string[]; onClose: () => void; onSent: (fileIds: string[]) => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [pre, setPre] = useState<Preview | null>(null);
  const [rows, setRows] = useState<Preview['files']>([]);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Result[] | null>(null);
  useEffect(() => {
    p.api.post<Preview>(`/api/projects/${p.id}/review/preview`, { kind: p.kind, fileIds: p.fileIds }).then((r) => {
      setPre(r);
      setRows(r.files);
    }, setErr);
  }, []);
  const draft = p.kind === 'draft';
  const set = (id: string, patch: Partial<Preview['files'][number]>) => setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const counts = new Map<string, number>();
  // files for a new video with the same name are one video, as on the server
  const key = (r: Preview['files'][number]) => (r.videoId === NEW ? `new:${r.newName.toLowerCase()}` : r.videoId);
  for (const r of rows) {
    const k = key(r);
    if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const twice = draft && [...counts.values()].some((n) => n > 1);
  const blocked = !rows.length || rows.some((r) => !r.videoId || r.problem) || twice;
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await p.api.post<{ results: Result[] }>(`/api/projects/${p.id}/review/submit`, { kind: p.kind, files: rows.map((x) => ({ fileId: x.id, videoId: x.videoId, ...(draft ? {} : { format: x.format }) })), ...(pre?.moveToHub ? { moveToHub: true } : {}) });
      setResults(r.results);
      p.onSent(r.results.filter((x) => x.ok).flatMap((x) => x.fileIds));
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  const title = draft ? t.sendForReview : t.sendFinal;
  return (
    <Dialog
      title={title}
      onClose={p.onClose}
      foot={
        results ? (
          <button type="button" class="btn btn-primary" onClick={p.onClose}>
            {t.done}
          </button>
        ) : (
          <>
            <button type="button" class="btn" onClick={p.onClose}>
              {t.cancel}
            </button>
            <button type="button" class="btn btn-primary" disabled={!pre || blocked || busy} onClick={() => void submit()}>
              {busy ? t.sending : pre?.moveToHub ? moveToHubLabel(lang) : t.sendForReviewNow}
            </button>
          </>
        )
      }
    >
      {results ? null : (
        <Tip id="naming" title={TIPS[lang].namingTitle}>
          {TIPS[lang].naming}
        </Tip>
      )}
      {results ? (
        <ul class="pfl-res">
          {results.map((r) => (
            <li>
              <strong>{r.videoName}</strong>{' '}
              {r.ok ? (
                <span class="small">
                  {r.versionId && r.videoId ? (
                    <>
                      {t.inHub}
                      <a href={`/projects/${p.id}/review/${r.videoId}?version=${r.versionId}`} onClick={(e) => (e.preventDefault(), navigate(`/projects/${p.id}/review/${r.videoId}?version=${r.versionId}`))}>
                        {t.openReview}
                      </a>
                    </>
                  ) : null}
                </span>
              ) : (
                <span class="small pfl-err" role="alert">
                  {t.notSent}
                  {r.error}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : err && !pre ? (
        <Callout tone="warn" title={err instanceof ApiError ? err.message : t.unreadable} />
      ) : !pre ? (
        <Loading label={t.readingFiles} />
      ) : (
        <>
          {pre.moveToHub ? <MoveToHubNote move={pre.moveToHub} /> : null}
          <p class="small">
            {draft ? t.draftInfo : t.finalInfo}
          </p>
          <ul class="pfl-rows">
            {rows.map((r) => (
              <li class="pfl-row">
                <span class="pfl-row-name">
                  <span title={r.name}>{r.name}</span>
                  {r.problem ? <span class="small pfl-err">{r.problem}</span> : null}
                  <Guess row={r} suggested={pre.files.find((x) => x.id === r.id)?.videoId ?? null} t={t} />
                </span>
                <span class="pfl-row-ctl">
                  <select class="input" aria-label={t.videoFor(r.name)} value={r.videoId ?? ''} onChange={(e) => set(r.id, { videoId: (e.target as HTMLSelectElement).value || null })}>
                    <option value="">{t.pickVideo}</option>
                    {pre.videos.map((v) => (
                      <option value={v.id}>{v.name}</option>
                    ))}
                    <option value={NEW}>{t.newVideoOpt(r.newName)}</option>
                  </select>
                  {draft ? null : <input class="input pfl-fmt" aria-label={t.formatOf(r.name)} value={r.format ?? ''} maxLength={20} onInput={(e) => set(r.id, { format: (e.target as HTMLInputElement).value })} />}
                  <button type="button" class="btn btn-sm btn-ghost" aria-label={t.remove(r.name)} onClick={() => setRows(rows.filter((x) => x.id !== r.id))}>
                    <Icon name="x" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
          {twice ? <p class="field-error small">{t.onePerVideo}</p> : null}
          {err ? <p class="field-error small">{err instanceof Error ? err.message : String(err)}</p> : null}
        </>
      )}
    </Dialog>
  );
}
